import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { User, Session, AuthError, AuthResponse, AuthTokenResponsePassword, AuthOtpResponse } from '@supabase/supabase-js';
import { supabase, supabaseInitError } from '@/integrations/supabase/client';
import { identifyUser, clearUser } from '@/lib/error-tracking';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthTokenResponsePassword>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Stub provider used when Supabase env vars are missing (e.g. preview deploys) */
function AuthProviderStub({ children }: { children: ReactNode }) {
  const stubError = new AuthError(supabaseInitError!);
  return (
    <AuthContext.Provider value={{
      user: null,
      session: null,
      loading: false,
      signIn: async () => ({ data: { user: null, session: null }, error: stubError }),
      signInWithMagicLink: async () => ({ error: stubError }),
      signOut: async () => ({ error: stubError }),
      resetPassword: async () => ({ error: stubError }),
      updatePassword: async () => ({ error: stubError }),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthProviderInner({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
      if (session?.user) { identifyUser(session.user.id, session.user.email ?? undefined); } else { clearUser(); }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) { identifyUser(session.user.id, session.user.email ?? undefined); } else { clearUser(); }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    // Clear any impersonation state to prevent stale banners on next login
    sessionStorage.removeItem('impersonating_from');
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signInWithMagicLink,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (supabaseInitError) return <AuthProviderStub>{children}</AuthProviderStub>;
  return <AuthProviderInner>{children}</AuthProviderInner>;
}
